import React, { useState, useCallback } from 'react';
import Cropper from 'react-easy-crop';
import imageCompression from 'browser-image-compression';

// Helper to create an HTMLImageElement from a URL/base64
const createImage = (url) =>
    new Promise((resolve, reject) => {
        const image = new Image();
        image.addEventListener('load', () => resolve(image));
        image.addEventListener('error', (error) => reject(error));
        image.setAttribute('crossOrigin', 'anonymous');
        image.src = url;
    });

// Pixel crop -> Blob (JPEG)
const getCroppedImg = async (imageSrc, pixelCrop) => {
    if (!pixelCrop) return null;
    const image = await createImage(imageSrc);
    const canvas = document.createElement('canvas');
    const ctx = canvas.getContext('2d');

    canvas.width = pixelCrop.width;
    canvas.height = pixelCrop.height;

    ctx.drawImage(
        image,
        pixelCrop.x,
        pixelCrop.y,
        pixelCrop.width,
        pixelCrop.height,
        0,
        0,
        pixelCrop.width,
        pixelCrop.height
    );

    return new Promise((resolve) => {
        canvas.toBlob((blob) => resolve(blob), 'image/jpeg', 0.95);
    });
};

// Store-specific image crop modal: returns compressed base64 image via onCropComplete(result)
// Props:
// imageSrc: source image (base64 or url)
// onClose: close handler
// onCropComplete: callback(base64) invoked after successful crop & compression
// aspect: numeric aspect ratio (default 1). For cover images pass 16/9,
//   for a portrait/mobile crop pass 9/16, etc.
// mode: optional string 'profile' | 'cover' | 'free' used for UI sizing tweaks.
//   'free' keeps the image's natural aspect ratio, so both portrait and
//   landscape uploads are preserved as-is (no forced orientation).
// title / hint: optional strings to override the header and footer copy
//   (used by callers that crop to a specific aspect, e.g. backgrounds).
export const StoreImageCropModal = ({ imageSrc, onClose, onCropComplete, aspect = 1, mode = 'profile', title, hint }) => {
    const [crop, setCrop] = useState({ x: 0, y: 0 });
    const [zoom, setZoom] = useState(1);
    const [croppedAreaPixels, setCroppedAreaPixels] = useState(null);
    const [processing, setProcessing] = useState(false);
    // In 'free' mode the effective aspect follows the uploaded image so
    // portrait and landscape are both kept; falls back to the prop until
    // the image has loaded.
    const [naturalAspect, setNaturalAspect] = useState(null);
    const isFree = mode === 'free';
    const effectiveAspect = isFree ? (naturalAspect || aspect) : aspect;

    const onMediaLoaded = useCallback((mediaSize) => {
        if (mediaSize?.naturalWidth && mediaSize?.naturalHeight) {
            setNaturalAspect(mediaSize.naturalWidth / mediaSize.naturalHeight);
        }
    }, []);

    const onCropCompleteCallback = useCallback((_, croppedPixels) => {
        setCroppedAreaPixels(croppedPixels);
    }, []);

    const handleCropConfirm = async () => {
        if (!croppedAreaPixels) {
            alert('בחר אזור לחיתוך קודם');
            return;
        }
        try {
            setProcessing(true);
            const croppedBlob = await getCroppedImg(imageSrc, croppedAreaPixels);
            if (!croppedBlob) throw new Error('Crop failed');

            // Compress – allow larger dimension for wide cover images
            const options = {
                maxSizeMB: 0.6,
                maxWidthOrHeight: aspect === 1 ? 1024 : 1600,
                // 'free' uploads can be tall portraits — keep them legible
                ...(isFree ? { maxWidthOrHeight: 1600 } : {}),
                useWebWorker: true,
            };
            const compressedBlob = await imageCompression(croppedBlob, options);

            // To base64
            const reader = new FileReader();
            reader.onloadend = () => {
                onCropComplete?.(reader.result);
            };
            reader.readAsDataURL(compressedBlob);
        } catch (err) {
            console.error('Error cropping image:', err);
            alert('שגיאה בחיתוך התמונה');
        } finally {
            setProcessing(false);
        }
    };

    // Dynamic height: portrait crops need a taller stage so the tall frame
    // fits; square is medium; wide/panoramic is shorter.
    const cropAreaHeightClass = isFree
        ? 'h-72'
        : (effectiveAspect < 0.85 ? 'h-96' : (effectiveAspect <= 1.05 ? 'h-64' : 'h-56'));
    // Portrait targets show better fully contained (pan/zoom to crop);
    // wide targets fill horizontally as before.
    const cropObjectFit = isFree
        ? 'contain'
        : (effectiveAspect < 1 ? 'contain' : 'horizontal-cover');

    return (
        <div
            className="fixed inset-0 bg-black/60 z-[60] flex items-center justify-center p-6"
            onClick={onClose}
        >
            <div
                className="w-full max-w-md bg-white rounded-xl shadow-2xl overflow-hidden border border-line"
                onClick={(e) => e.stopPropagation()}
            >
                {/* Header */}
                <div className="px-4 py-3 border-b border-line flex items-center justify-between bg-white">
                    <h3 className="text-sm font-semibold text-ink-strong">
                        {title
                            ? title
                            : (isFree
                                ? 'התאמת תמונה (לאורך או לרוחב)'
                                : (mode === 'cover' ? 'חיתוך תמונת כיסוי (16:9)' : 'חיתוך תמונת פרופיל (ריבוע)'))}
                    </h3>
                    <button
                        type="button"
                        onClick={onClose}
                        className="text-ink-subtle hover:text-ink-muted"
                        aria-label="סגור"
                    >
                        ✕
                    </button>
                </div>
                {/* Cropper area */}
                <div className={`relative w-full ${cropAreaHeightClass} bg-surface-muted`}>
                    <Cropper
                        image={imageSrc}
                        crop={crop}
                        zoom={zoom}
                        aspect={effectiveAspect}
                        onCropChange={setCrop}
                        onZoomChange={setZoom}
                        onCropComplete={onCropCompleteCallback}
                        onMediaLoaded={onMediaLoaded}
                        objectFit={cropObjectFit}
                        showGrid={false}
                    />
                </div>
                {/* Controls */}
                <div className="p-4 space-y-5">
                    <div className="space-y-2">
                        <label className="block text-xs font-medium text-ink-muted">זום</label>
                        <div className="flex items-center gap-3">
                            <button
                                type="button"
                                onClick={() => setZoom(z => Math.max(1, parseFloat((z - 0.2).toFixed(2))))}
                                disabled={processing || zoom <= 1}
                                className="px-2 py-1 rounded-md border border-line-strong text-ink text-xs hover:bg-surface-muted disabled:opacity-40"
                                aria-label="Zoom out"
                            >-
                            </button>
                            <input
                                type="range"
                                min={1}
                                max={3}
                                step={0.05}
                                value={zoom}
                                onChange={(e) => setZoom(parseFloat(e.target.value))}
                                className="flex-1 accent-warning-500"
                                aria-label="Zoom level"
                            />
                            <button
                                type="button"
                                onClick={() => setZoom(z => Math.min(3, parseFloat((z + 0.2).toFixed(2))))}
                                disabled={processing || zoom >= 3}
                                className="px-2 py-1 rounded-md border border-line-strong text-ink text-xs hover:bg-surface-muted disabled:opacity-40"
                                aria-label="Zoom in"
                            >+
                            </button>
                        </div>
                        <div className="text-[11px] text-ink-subtle text-center">{zoom.toFixed(2)}x</div>
                    </div>
                    <div className="flex gap-2">
                        <button
                            type="button"
                            onClick={onClose}
                            className="flex-1 px-3 py-2 text-sm border border-line-strong rounded-md hover:bg-surface-muted transition-colors text-ink-strong"
                            disabled={processing}
                        >
                            ביטול
                        </button>
                        <button
                            type="button"
                            onClick={handleCropConfirm}
                            className="flex-1 px-3 py-2 text-sm bg-warning-500 text-white rounded-md hover:bg-warning-600 transition-colors disabled:opacity-50"
                            disabled={processing}
                        >
                            {processing ? 'מעבד...' : 'חתוך והמשך'}
                        </button>
                    </div>
                    {hint ? (
                        <p className="text-[11px] text-ink-subtle text-center">{hint}</p>
                    ) : mode === 'cover' ? (
                        <p className="text-[11px] text-ink-subtle text-center">התמונה תיחתך ביחס 16:9 ותדחס לגודל אופטימלי</p>
                    ) : isFree ? (
                        <p className="text-[11px] text-ink-subtle text-center">התמונה נשמרת לפי הכיוון המקורי (לאורך או לרוחב) ונדחסת לגודל אופטימלי</p>
                    ) : null}
                </div>
            </div>
        </div>
    );
};

export default StoreImageCropModal;
